import { useColorFromTheme } from '@droplet-tech-code/core-elements/module/theme';
import { Divider } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import { Icon } from '@droplet-tech-code/core-elements/module/ui/Icon';
import { SelectInputBase } from '@droplet-tech-code/core-elements/module/ui/Inputs/SelectInput/SelectInput';
import { useModalVerify } from '@droplet-tech-code/core-elements/module/ui/Modal';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import { View } from '@droplet-tech-code/core-elements/module/ui/View';
import { handleResponse } from '@droplet-tech-code/core-elements/module/utils/error';
import {
  copyToClipboard,
  isWeb,
} from '@droplet-tech-code/core-elements/module/utils/utils.helper';
import { BookingClient, SupportRequestBackendClient } from '@naus-code/naus-admin-types';
import { SupportStatus } from '@naus-code/naus-client-types';
import React, { useCallback, useState } from 'react';

import { LiveDateText } from '~/components/DateText';
import { PressableOption } from '~/components/SupportConversation/InputPanel/Conversation.Common';
import { AppText } from '~/components/Text';
import { useGetAuthUserRules } from '~/screens/Auth/Auth.Context';
import { bookingApi } from '~/screens/Support/Bookings/Bookings.api';
import { getPriceWithSymbol } from '~/screens/Support/Purchases/purchase.utils';
import { useInvalidateClosedSupportRequestList } from '~/screens/Support/Requests/Closed/SupportRequest.List';
import {
  useInvalidateCustomerRequestListByEmail,
  useInvalidateCustomerRequestListById,
} from '~/screens/Support/Requests/Customer/SupportRequest.List';
import { useInvalidatePausedSupportRequestList } from '~/screens/Support/Requests/Pending/SupportRequest.List';
import { supportRequestActionsApi } from '~/screens/Support/Requests/supportRequestActions.api';
import { translate } from '~/utils/translation.utils';

import { RatingInfo } from '../../Ratings/RatingItem';
import { CopyableText } from '../../Text/CopyableText';
import { useAssignPurchase } from '../Modals/AssignPurchase';
import { useAssignUser } from '../Modals/AsssignUser';
import { useCreatePaymentCharge } from '../Modals/CreatePayment';
import { useCreatePaymentRefund } from '../Modals/CreateRefund';
import { useRefundPaymentDataModal } from '../Modals/RefundPaymentData';
import { formatMapToFormData, getColorFromStatus } from './SupportRequestStatus.utils';
import { ClickableListItem } from './utils';
import { useGetSupportItemAdminUser } from '~/screens/Support/Requests/utils/supportRequest.utils';
import { useGetKoalaQuote } from '../Modals/Koala.NewQuote';
import { dayjs } from '@droplet-tech-code/core-elements/module/utils/utils.date';
import { DATE_TIME_SERVER_FORMAT } from '~/utils/date.utils';
import { Field } from '~/components/Card/Field';
import { CfarQuoteSection } from './detailsSections/detailsSections';

export const SupportRequestDetailsHeader = () => {
  return (
    <>
      <View
        px="2.5"
        py="3"
        bg="monochrome-extraLight"
        justify="center"
        style={{ maxWidth: isWeb ? 400 : undefined, height: '100%' }}
      >
        <AppText.MiscTag type="heading1">
          {translate('supportRequests.details')}
        </AppText.MiscTag>
      </View>
      <Divider bg="monochrome-light" style={{ maxWidth: isWeb ? 400 : undefined }} />
    </>
  );
};

export const SupportRequestDetailsContent = ({
  check,
  supportRequestInfo,
}: {
  check: string;
  supportRequestInfo: SupportRequestBackendClient;
}) => {
  const { open: openAssignPurchase } = useAssignPurchase();
  const supportId = supportRequestInfo._id;
  const invalidateClosed = useInvalidateClosedSupportRequestList();
  const invalidatePaused = useInvalidatePausedSupportRequestList();
  const invalidateById = useInvalidateCustomerRequestListById();
  const invalidateByEmail = useInvalidateCustomerRequestListByEmail();

  const [sendCustomerReminder] =
    supportRequestActionsApi.useSendCustomerReminderMutation();

  const [setSupportRequestStatus] =
    supportRequestActionsApi.useChangeSupportStatusMutation();

  const onSendReminder = useCallback(
    async (method: {
      sendEmail: boolean;
      sendSms: boolean;
      sendNotification: boolean;
    }) => {
      const response = await sendCustomerReminder({
        supportId,
        check,
        notification: method.sendNotification,
        email: method.sendEmail,
        sms: method.sendSms,
      });
      handleResponse({
        response,
        successMessage: translate('supportRequests.reminderSent'),
      });
    },
    [check, supportId],
  );
  const {
    _customerId,
    dateUpdated,
    timestampLastNotified,
    p_email,
    p_customerName,
    status,
    ref,
  } = supportRequestInfo;
  const feedback = supportRequestInfo.feedback;

  const changeStatus = useCallback(
    async (status: SupportStatus) => {
      const response = await setSupportRequestStatus({
        status,
        check,
        supportId,
      });

      handleResponse({
        response,
        onSuccess: async () => {
          await Promise.all([
            invalidateClosed.invalidate(),
            invalidatePaused.invalidate(),
            invalidateById.invalidate(_customerId),
            invalidateByEmail.invalidate(p_email),
          ]).catch(console.error);
        },
      });
    },
    [check, supportId, _customerId, p_email],
  );
  //======================================//

  const colorKeyStatus = getColorFromStatus(status);
  const statusColor = useColorFromTheme(colorKeyStatus);

  return (
    <VStack space="2.5" flex={1} style={{ maxWidth: isWeb ? 400 : undefined }}>
      {!supportRequestInfo._customerIdClient && (
        <View px="3">
          <VStack p="2.5" br={20} bg="warning-extraLight">
            <AppText.MiscTag flex={1} align="center" color="warning-dark">
              {translate('supportRequests.hiddenFromCustomer')}
            </AppText.MiscTag>
          </VStack>
        </View>
      )}
      <VStack px="3" space="1">
        <View px="1">
          <View
            style={{
              width: '100%',
              backgroundColor: statusColor,
              height: 6,
              borderRadius: 6,
              alignSelf: 'center',
            }}
          />
        </View>
        <SelectInputBase
          values={[status]}
          setValues={(nv) => {
            if (nv !== undefined) {
              changeStatus(nv[0] as SupportStatus);
            }
          }}
          iconEnd={
            <HStack space="3">
              <View
                style={{
                  backgroundColor: statusColor,
                  width: 18 * 3,
                  height: 18,
                  borderRadius: 999,
                }}
              />
              <Icon icon="chevron-down" color="black" />
            </HStack>
          }
          options={formatMapToFormData(supportRequestInfo)}
          type="single-select"
          noFeedback
          feedback=""
        />
      </VStack>
      <Divider />
      <HStack px="3">
        <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
          {translate('supportRequests.updated')}
        </AppText.ExtraSmall>
        <LiveDateText dateTime={dateUpdated} formatOption="fullDateTime" type="small" />
      </HStack>
      <Divider />
      <HStack px="3">
        <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
          {translate('supportRequests.lastReminded')}
        </AppText.ExtraSmall>
        {timestampLastNotified ? (
          <LiveDateText
            dateTime={timestampLastNotified}
            formatOption="fullDateTime"
            type="small"
          />
        ) : null}
      </HStack>
      <Divider />
      <HStack px="3">
        <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
          {translate('supportRequests.supportEmail')}
        </AppText.ExtraSmall>
        <CopyableText value={p_email} />
      </HStack>
      <Divider />
      <HStack px="3">
        <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
          {translate('supportRequests.customer')}
        </AppText.ExtraSmall>
        <CopyableText value={p_customerName} />
      </HStack>
      <Divider />
      <HStack px="3">
        <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
          {translate('customer.supportRef')}
        </AppText.ExtraSmall>
        <CopyableText value={ref} />
      </HStack>
      <Divider />
      {supportRequestInfo.trackId ? (
        <>
          <HStack px="3">
            <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
              {translate('purchases.trackingInfo.trackId')}
            </AppText.ExtraSmall>
            <CopyableText value={supportRequestInfo.trackId} />
          </HStack>
          <Divider />
        </>
      ) : null}
      <KoalaQuoteSection supportRequestInfo={supportRequestInfo} check={check} />
      <CfarQuoteSection
        key={supportId}
        supportRequestInfo={supportRequestInfo}
        check={check}
      />
      <PendingOperatorSection supportRequestInfo={supportRequestInfo} check={check} />
      <Divider />
      <PaymentSection supportRequestInfo={supportRequestInfo} check={check} />
      <Divider />
      <RefundSection supportRequestInfo={supportRequestInfo} check={check} />

      <SendReminder
        canNotify={!!supportRequestInfo.pushIds?.length}
        onSendReminder={onSendReminder}
      />
      <Divider />

      <ClickableListItem
        icon="ticket"
        color="primary-mid"
        onPress={() => {
          openAssignPurchase(supportRequestInfo, check);
        }}
      >
        {` ${translate('supportRequests.assignPurchase')}`}
      </ClickableListItem>
      <Divider />
      <AdminUserInfo supportRequestInfo={supportRequestInfo} check={check} />
      <Divider />
      {feedback && (
        <VStack space="2.5">
          <View px="2.5">
            <RatingInfo feedback={feedback} />
          </View>
          <Divider />
        </VStack>
      )}
    </VStack>
  );
};

const AdminUserInfo = ({
  supportRequestInfo,
  check,
}: {
  supportRequestInfo: SupportRequestBackendClient;
  check: string;
}) => {
  const { open: openAssignUser } = useAssignUser();
  const { _id: supportId, adminUserName } = supportRequestInfo;
  const adminUserId = useGetSupportItemAdminUser(supportRequestInfo);

  const canAssign = useGetAuthUserRules('assignSupportRequests');

  return (
    <HStack>
      <View px="2.5" flex={1}>
        <Text.Small color={adminUserId ? 'success-light' : 'monochrome'}>
          {adminUserId ? adminUserName : translate('supportRequests.unassigned')}
        </Text.Small>
      </View>
      {canAssign && (
        <ClickableListItem
          icon="support-agent"
          color="primary-mid"
          onPress={() => {
            openAssignUser(check, supportId);
          }}
        >
          {`${translate('supportRequests.assignUser')}`}
        </ClickableListItem>
      )}
    </HStack>
  );
};

const SendReminder = ({
  onSendReminder,
  canNotify,
}: {
  canNotify: boolean;
  onSendReminder: ({
    sendEmail,
    sendSms,
    sendNotification,
  }: {
    sendEmail: boolean;
    sendSms: boolean;
    sendNotification: boolean;
  }) => void;
}) => {
  const [sendSms, setSendSms] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);
  return (
    <HStack space="2.5" pl="3">
      <HStack>
        <PressableOption
          disabled={!canNotify}
          state={sendNotification}
          text={translate('supportRequests.notification')}
          onPress={() => setSendNotification(!sendNotification)}
        />
        <PressableOption
          state={sendEmail}
          text={translate('supportRequests.email')}
          onPress={() => setSendEmail(!sendEmail)}
        />
        <PressableOption
          state={sendSms}
          text={translate('supportRequests.sms')}
          onPress={() => setSendSms(!sendSms)}
        />
      </HStack>
      <View flex={1} />
      <ClickableListItem
        icon="bell"
        color="primary-mid"
        onPress={() => onSendReminder({ sendEmail, sendSms, sendNotification })}
      >
        {` ${translate('supportRequests.sendReminder')}`}
      </ClickableListItem>
    </HStack>
  );
};

const RefundSection = ({
  supportRequestInfo,
  check,
}: {
  supportRequestInfo: SupportRequestBackendClient;
  check: string;
}) => {
  const canApprove = useGetAuthUserRules('canApprovePendingRefund');
  const canCreatePendingRefund = useGetAuthUserRules('canCreatePendingRefund');

  const { open: openRefundDetails } = useRefundPaymentDataModal();

  if (!canCreatePendingRefund) {
    return null;
  }

  const supportId = supportRequestInfo._id;
  const { openModal: openCreateRefund } = useCreatePaymentRefund();
  const { verifyAction } = useModalVerify();
  const { data } = bookingApi.useGetBookingQuery(supportRequestInfo.bookingId || '', {
    skip: !supportRequestInfo.bookingId,
  });

  const booking = data?.booking;

  const [cancelPendingRefund, { isLoading: isCancellingRefund }] =
    supportRequestActionsApi.useCancelPendingRefundMutation();
  const [approvePendingRefund, { isLoading: isApprovingRefund }] =
    supportRequestActionsApi.useApprovePendingRefundMutation();
  //========= Callbacks ============//
  const openCancelPendingRefund = useCallback(async () => {
    const verify = await verifyAction({
      title: translate('supportRequests.cancelPendingRefund'),
    });
    if (verify) {
      cancelPendingRefund({ supportId, check });
    }
  }, [check, supportId]);

  const openApprovePendingPayment = useCallback(async () => {
    const verify = await verifyAction({
      title: translate('supportRequests.approveRefund'),
    });
    if (verify) {
      approvePendingRefund({ supportId, check });
    }
  }, [check, supportId]);

  if (supportRequestInfo.pendingRefund) {
    const pendingRefund = supportRequestInfo.pendingRefund;
    const currency = getCurrencyFromPaymentSession(
      pendingRefund.paymentSessionId,
      booking,
    );

    return (
      <VStack space="2.5">
        <VStack br={15} bg={'primary-extraLight'} p="2.5">
          <HStack m="1" p="2.5" space="2">
            <AppText.ExtraSmall
              color="monochrome-extraDark"
              flex={1}
              onPress={() => openRefundDetails(pendingRefund, currency)}
            >
              {translate('supportRequests.pendingRefund')}
            </AppText.ExtraSmall>
            <AppText.Small>
              {getPriceWithSymbol(pendingRefund.value, currency || '')}
            </AppText.Small>
          </HStack>
          <HStack>
            {canApprove && (
              <ClickableListItem
                isLoading={isApprovingRefund}
                disable={isApprovingRefund || isCancellingRefund}
                color="primary-mid"
                onPress={openApprovePendingPayment}
              >
                {`${translate('supportRequests.approve')}`}
              </ClickableListItem>
            )}
            <View flex={1} />
            <ClickableListItem
              color="primary-mid"
              isLoading={isCancellingRefund}
              disable={isApprovingRefund || isCancellingRefund}
              onPress={openCancelPendingRefund}
            >
              {`${translate('supportRequests.cancelRefund')}`}
            </ClickableListItem>
          </HStack>
        </VStack>
        <Divider />
      </VStack>
    );
  }

  return (
    <VStack space="2.5">
      <ClickableListItem
        icon="custom-refundable"
        color="primary-mid"
        onPress={() => {
          openCreateRefund({
            supportRequestInfo,
            check,
          });
        }}
      >
        {` ${translate('supportRequests.createRefund')}`}
      </ClickableListItem>
      <Divider />
    </VStack>
  );
};

const KoalaQuoteSection = ({
  supportRequestInfo,
  check,
}: {
  supportRequestInfo: SupportRequestBackendClient;
  check: string;
}) => {
  const { data } = bookingApi.useGetBookingQuery(supportRequestInfo.bookingId || '', {
    skip: !supportRequestInfo.bookingId,
  });

  const { bookingId, _id: supportId } = supportRequestInfo;

  const { open: openKoalaQuoteModal } = useGetKoalaQuote();
  const koalaSessionType = supportRequestInfo.manualKoalaSession
    ? translate('supportRequests.koala.manual')
    : translate('supportRequests.koala.auto');
  const currentSession =
    supportRequestInfo.manualKoalaSession ||
    //@ts-expect-error
    supportRequestInfo.requestOptions?.bookingKoalaSession;

  const { verifyAction } = useModalVerify();
  const [subscribeKoala, { isLoading: isSubbing }] =
    supportRequestActionsApi.useSubscribeKoalaQuoteMutation();

  const [cancelKoala, { isLoading: isCancelling }] =
    supportRequestActionsApi.useCancelKoalaQuoteMutation();

  // Verify modal
  const openQuoteActionVerify = useCallback(
    async (subscribeQuote: boolean) => {
      await verifyAction({
        title: subscribeQuote
          ? translate('supportRequests.subscribeKoalaVerify')
          : translate('supportRequests.cancelKoalaVerify'),
      }).then((verify) => {
        if (verify) {
          if (subscribeQuote) {
            subscribeKoala({
              supportId,
              check,
              bookingId: bookingId!,
            });
            return;
          }
          cancelKoala({ supportId, check, bookingId: bookingId! });
        }
      });
    },
    [check, supportId],
  );

  if (!data?.booking || !data?.booking.bookingKoala) {
    return null;
  }

  if (currentSession?.errors?.length) {
    const lastError = currentSession.errors?.at(-1);
    return (
      <VStack space="2.5" p="2.5">
        <VStack br={15} bg={'error-extraLight'} p="2.5">
          <VStack m="1" space="2">
            <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
              {`${translate('supportRequests.koalaSession')} - ${koalaSessionType}`}
            </AppText.ExtraSmall>
            <Text.Small>{lastError}</Text.Small>
          </VStack>
          <HStack style={{ alignContent: 'stretch' }} flex={1}>
            <View flex={1} />
            <ClickableListItem
              color="success-dark"
              isLoading={isCancelling}
              onPress={async () => await openQuoteActionVerify(false)}
            >
              {` ${translate('supportRequests.cancelQuote')}`}
            </ClickableListItem>
          </HStack>
        </VStack>
        <Divider />
        <ClickableListItem
          icon="euro"
          color="primary-mid"
          onPress={() => {
            openKoalaQuoteModal(supportRequestInfo, check);
          }}
        >
          {` ${translate('supportRequests.newKoalaQuote')}`}
        </ClickableListItem>
      </VStack>
    );
  }

  return (
    <VStack space="2.5">
      {currentSession ? (
        <VStack space="2.5" p="2.5">
          <VStack br={15} bg={'success-extraLight'} p="2.5">
            <VStack m="1" space="2">
              <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
                {`${translate('supportRequests.koalaSession')} - ${koalaSessionType}`}
              </AppText.ExtraSmall>
              <Field
                label={translate('supportRequests.koalaDiff')}
                labelType="extraSmall"
                labelColor="black"
                value={getPriceWithSymbol(
                  currentSession.valueDiff,
                  data.booking.paymentSessions[0]?.paymentSummary.currency ?? '',
                )}
                valueType="extraSmall"
              />
              <Field
                label={translate('supportRequests.dateCreated')}
                labelColor="black"
                labelType="extraSmall"
                value={dayjs(currentSession.timestamp).format(DATE_TIME_SERVER_FORMAT)}
                valueType="extraSmall"
              />
            </VStack>
            <HStack style={{ alignContent: 'stretch' }} flex={1}>
              <ClickableListItem
                color="success-dark"
                isLoading={isSubbing}
                onPress={async () => await openQuoteActionVerify(true)}
              >
                {`${translate('supportRequests.subscribeQuote')}`}
              </ClickableListItem>
              <View flex={1} />
              <ClickableListItem
                color="success-dark"
                isLoading={isCancelling}
                onPress={async () => await openQuoteActionVerify(false)}
              >
                {` ${translate('supportRequests.cancelQuote')}`}
              </ClickableListItem>
            </HStack>
          </VStack>
          <Divider />
        </VStack>
      ) : null}
      <ClickableListItem
        icon="euro"
        color="primary-mid"
        onPress={() => {
          openKoalaQuoteModal(supportRequestInfo, check);
        }}
      >
        {` ${translate('supportRequests.newKoalaQuote')}`}
      </ClickableListItem>
      <Divider />
    </VStack>
  );
};

const PaymentSection = ({
  supportRequestInfo,
  check,
}: {
  supportRequestInfo: SupportRequestBackendClient;
  check: string;
}) => {
  const supportId = supportRequestInfo._id;

  const [cancelPendingPayment] =
    supportRequestActionsApi.useCancelPendingPaymentMutation();

  const { data } = bookingApi.useGetBookingQuery(supportRequestInfo.bookingId || '', {
    skip: !supportRequestInfo.bookingId,
  });

  const { verifyAction: verifyCancelPayment } = useModalVerify();

  const { openModal: openCreatePayment } = useCreatePaymentCharge();
  //========= Callbacks ============//
  const openCancelPendingPayment = useCallback(async () => {
    await verifyCancelPayment({
      title: translate('supportRequests.cancelPendingPayment'),
    }).then((verify) => {
      if (verify) {
        cancelPendingPayment({ supportId, check });
      }
    });
  }, [check, supportId]);

  if (supportRequestInfo.pendingPayment) {
    const { pendingPayment } = supportRequestInfo;
    return (
      <VStack br={15} bg={'warning-extraLight'} p="2.5">
        <HStack m="1" space="2">
          <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
            {translate('supportRequests.pendingPayment')}
          </AppText.ExtraSmall>
          <AppText.Small>
            {getPriceWithSymbol(pendingPayment.value, pendingPayment.currency)}
          </AppText.Small>
          <AppText.Small>
            {translate('supportRequests.service', {
              value: getPriceWithSymbol(
                pendingPayment.serviceFee +
                  (pendingPayment.koalaFee || 0) +
                  (pendingPayment.cfarFee || 0),
                pendingPayment.currency,
              ),
            })}
          </AppText.Small>
        </HStack>
        <HStack m="1" space="2">
          <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
            {translate('supportRequests.total')}
          </AppText.ExtraSmall>
          <AppText.SmallMedium>
            {getPriceWithSymbol(
              pendingPayment.value +
                pendingPayment.serviceFee +
                (pendingPayment.koalaFee || 0) +
                (pendingPayment.cfarFee || 0),
              pendingPayment.currency,
            )}
          </AppText.SmallMedium>
        </HStack>
        <HStack p="0">
          {(supportRequestInfo as any).paymentLink ? (
            <ClickableListItem
              icon="copy"
              color="primary-mid"
              onPress={() => copyToClipboard((supportRequestInfo as any).paymentLink)}
            >
              {`copy link`}
            </ClickableListItem>
          ) : null}
          <View flex={1} />
          <ClickableListItem
            icon="euro"
            color="primary-mid"
            onPress={async () => await openCancelPendingPayment()}
          >
            {` ${translate('supportRequests.cancelPendingPayment')}`}
          </ClickableListItem>
        </HStack>
      </VStack>
    );
  }

  return (
    <ClickableListItem
      icon="euro"
      color="primary-mid"
      onPress={() => {
        openCreatePayment({
          supportRequestInfo,
          booking: data?.booking,
          check,
        });
      }}
    >
      {` ${translate('supportRequests.createPayment')}`}
    </ClickableListItem>
  );
};

const PendingOperatorSection = ({
  supportRequestInfo,
  check,
}: {
  supportRequestInfo: SupportRequestBackendClient;
  check: string;
}) => {
  const supportId = supportRequestInfo._id;
  const [togglePendingResponse, { isLoading }] =
    supportRequestActionsApi.useTogglePendingOperatorResponseMutation();
  const isPending = supportRequestInfo.pendingOperatorResponse;
  return (
    <HStack flex={1} pl="2.5">
      {isPending ? (
        <HStack>
          <View bg="success-light" p="1" br={6}>
            <Text.Small color="white" bold>
              {translate('supportRequests.pendingOperatorResponse')}
            </Text.Small>
          </View>
        </HStack>
      ) : null}
      <View flex={1} />
      <ClickableListItem
        icon="vessel-type"
        color="primary-mid"
        isLoading={isLoading}
        onPress={() => {
          togglePendingResponse({
            supportId,
            check,
          });
        }}
      >
        {` ${translate('supportRequests.togglePendingOperator')} ${isPending ? 'off' : 'on'}`}
      </ClickableListItem>
    </HStack>
  );
};

const getCurrencyFromPaymentSession = (
  paymentSessionId: string,
  booking?: BookingClient,
) => {
  if (booking) {
    const paymentSession = booking.paymentSessions.find(
      (paymentSession) => paymentSession.id === paymentSessionId,
    );
    if (paymentSession) {
      return paymentSession.paymentSummary.currency;
    }
  }
  return '';
};
